import { Injectable } from '@angular/core';
import { FilterCriteria, IrdEntitySchema, IrdPropertyType } from '@ird/shared-base';
import { ActiveFilter, FilterFieldDefinition, FilterFieldType } from './filter-search-input.models';

const PROPERTY_TYPE_TO_FILTER_TYPE: Partial<Record<IrdPropertyType, FilterFieldType>> = {
  string: 'string',
  number: 'number',
  boolean: 'boolean',
  Date: 'date',
  enum: 'enum',
  select: 'enum',
  json: 'json',
};

@Injectable({ providedIn: 'root' })
export class FilterSearchService {
  /**
   * Derives FilterFieldDefinition[] from IrdEntitySchema.
   * Includes properties marked as filterable (having operations or values defined).
   * Operations default by type when not explicitly set.
   */
  getFilterFieldsFromMetadata<T>(metadata: IrdEntitySchema<T>): FilterFieldDefinition[] {
    return Object.values(metadata.properties)
      .filter((prop: any) => prop.filterable)
      .map((prop: any) => {
        const def: FilterFieldDefinition = {
          field: prop.nameAsString(),
          label: prop.getTranslationKey(),
          type: PROPERTY_TYPE_TO_FILTER_TYPE[prop.type as IrdPropertyType] ?? 'string',
        };
        if (prop.filterOperations?.length > 0) {
          def.operations = prop.filterOperations;
        }
        if (prop.allowedValues?.length > 0) {
          def.values = prop.allowedValues;
        }
        if (prop.filterJsonKeys?.length > 0) {
          def.jsonKeys = prop.filterJsonKeys;
        }
        return def;
      });
  }

  /**
   * Converts FilterCriteria[] (e.g., from URL params) to ActiveFilter[]
   * by resolving each criteria's field reference from the available field definitions.
   */
  criteriaToActiveFilters<T>(criteria: FilterCriteria<T>[], fields: FilterFieldDefinition[]): ActiveFilter[] {
    const fieldMap = new Map(fields.map((f) => [f.field, f]));
    return criteria.reduce<ActiveFilter[]>((acc, c) => {
      const fieldDef = fieldMap.get(c.field);
      if (!fieldDef) {
        return acc;
      }
      const matchedValue = fieldDef.values?.find((v) => v.value === c.value);
      const jsonLabel = fieldDef.type === 'json' && c.value ? this.decodeJsonFilterValue(c.value) : null;
      acc.push({
        field: fieldDef,
        operation: c.operation,
        value: c.value,
        values: c.values,
        displayValue: matchedValue?.label ?? jsonLabel ?? c.value ?? '',
      });
      return acc;
    }, []);
  }

  /**
   * Accepts `key=value` (or `key:value`) and returns `{"key":"value"}` — the wire format
   * the backend's JSON_CONTAINS operator expects on a JSONB map column.
   * If the text is already valid JSON matching `Record<string, string>`, it is passed through
   * unchanged. Returns `null` when the input can't be encoded.
   */
  encodeJsonFilterValue(text: string): string | null {
    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch {
      return this.encodeKeyValueFilterValue(text);
    }
    if (this.isStringRecord(parsed)) {
      return text;
    }
    return null;
  }

  /** Inverse of encodeJsonFilterValue, for chip labels: `{"MOTOR_TYPE":"IE3"}` -> `MOTOR_TYPE = IE3`. */
  decodeJsonFilterValue(value: string): string | null {
    let parsed: unknown;
    try {
      parsed = JSON.parse(value);
    } catch {
      return null;
    }
    if (!this.isStringRecord(parsed)) {
      return null;
    }
    return Object.entries(parsed)
      .map(([key, entry]) => `${key} = ${entry}`)
      .join(', ');
  }

  private isStringRecord(value: unknown): value is Record<string, string> {
    return (
      value !== null &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      Object.values(value as Record<string, unknown>).every((v) => typeof v === 'string')
    );
  }

  private encodeKeyValueFilterValue(text: string): string | null {
    const separatorIndex = this.findKeyValueSeparator(text);
    if (separatorIndex < 0) {
      return null;
    }
    const key = text.slice(0, separatorIndex).trim();
    const value = text.slice(separatorIndex + 1).trim();
    if (!key || !value) {
      return null;
    }
    return JSON.stringify({ [key]: value });
  }

  private findKeyValueSeparator(text: string): number {
    const equalsIndex = text.indexOf('=');
    const colonIndex = text.indexOf(':');
    if (equalsIndex < 0) {
      return colonIndex;
    }
    if (colonIndex < 0) {
      return equalsIndex;
    }
    return Math.min(equalsIndex, colonIndex);
  }
}
